import { useCallback, useEffect, useState, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import styles from './GrantOnboardingAccess.module.css';
import { GoogleOAuthProvider } from '@react-oauth/google';
import Config from '../../../utils/Config';
import getCMResponse, { revokeDataSources } from '../../../utils/api_manager';
import { buildProjectPath } from '../../../utils/projectPath';
import PageLoader from '../../../components/PageLoader/PageLoader';

// Existing auth components (OAuth flow unchanged)
import GoogleAnalyticsAuth from '../../../components/Datasources/GoogleAnalyticsAuth/GoogleAnalyticsAuth';
import GoogleAdsAuth from '../../../components/Datasources/GoogleAdsAuth/GoogleAdsAuth';
import GoogleSearchConsoleAuth from '../../../components/Datasources/GoogleSearchConsoleAuth/GoogleSearchConsoleAuth';
import GoogleMerchantCenterAuth from '../../../components/Datasources/GoogleMerchantCenterAuth/GoogleMerchantCenterAuth';
import MetaAuth from '../../../components/Datasources/MetaAuth/MetaAuth';
import ShopifyOnboardingAuth from '../../../components/Datasources/Shopify/ShopifyOnboardingAuth';
import ShopifyUrlDialog from './ShopifyUrlDialog';
import shopifyIconAsset from '../../../assets/icons/shopify.svg';
import { GoogleAnalyticsIcon, GOOGLE_ANALYTICS_URL } from '../../../components/BrandLogos/GoogleAnalytics';
// TRD grant-access-connector-branding/trd.md, R1/R2 -- real, separately
// sourced Meta Brand Resource Center assets for the two tiles the old single
// "Meta" combo tile is split into. See components/BrandLogos/Facebook and
// .../Instagram for sourcing details (D9/D10 -- these are NOT the
// pre-existing hand-drawn assets/icons/facebook-pages.svg / instagram.svg).
import { FacebookIcon } from '../../../components/BrandLogos/Facebook';
import { InstagramIcon } from '../../../components/BrandLogos/Instagram';

/* ─── Icon helper ────────────────────────────────────────────────── */
const Icon = ({ name, size = 18, color = 'inherit', style = {} }) => (
  <span
    className="material-symbols-rounded"
    style={{ fontSize: size, color, lineHeight: 1, ...style }}
  >
    {name}
  </span>
);

/* ─── Brand icons ────────────────────────────────────────────────── */
// Google Analytics uses the real official brand asset (icon-only SVG mark,
// colors locked) via components/BrandLogos/GoogleAnalytics — see PRD
// grant-access-connector-branding, G16.
const GAIcon = GoogleAnalyticsIcon;

// TRD grant-access-connector-branding/trd.md, R6 (D4/D5/G12/G17) -- Google
// Ads, Search Console, and Merchant Center no longer render ANY Google-owned
// icon asset, hand-drawn or official: Google's Partner Marketing Hub
// approval for these three product icons remains unconfirmed, and per the
// PRD's final adopted resolution (2026-08-09, Section 8 #4) CliqMetrics will
// never pursue that approval -- generic/text-only labeling is the permanent
// path, not a contingency. This is a neutral, non-brand-implying glyph
// (reusing the file's own existing `Icon` helper, already used elsewhere in
// this component for the connected-checkmark/locked-badge icons) -- it
// cannot be mistaken for any Google mark. The product's full name stays as
// real text (platform.name/description) -- only the icon asset is affected.
const GenericConnectorIcon = ({ size = 22 }) => (
  <Icon name="link" size={size} color="var(--color-on-surface-muted)" />
);

const ShopifyIcon = ({ size = 32 }) => (
  <img src={shopifyIconAsset} alt="" width={size} height={size} />
);

/* ─── Platform definitions ───────────────────────────────────────── */
// Shopify is intentionally first: it must be the first platform a project
// connects (its shop domain, captured at project creation, is what every
// other data import is scoped against). See `shopifyConnected` gating below —
// every other tile is locked until this one shows providerProjectId != null.
// TRD grant-access-connector-branding/trd.md, R3 -- every non-Shopify tile
// now carries `ctaLabel` (button text) and `ctaVariant` (brand-color fill)
// fields, consumed by PlatformTile's footer CTA button. Shopify intentionally
// has neither -- its tile keeps its existing pixel-for-pixel-unchanged
// unbranded footer treatment (PRD Section 2), so PlatformTile special-cases
// `id === 'SHOPIFY'` rather than reading these fields for it.
//
// R4 -- `isGoogleProduct: true` on the four Google tiles switches
// PlatformTile to the CliqMetrics-eyebrow-dominant layout (G13/G14): a
// "Cliqmetrics" wordmark eyebrow renders as the dominant top element, with
// the Google product demoted to a small icon + label row beneath it. This
// applies uniformly to all four, including the three generic-icon ones
// (R6) -- the demoted "icon" for those three is GenericConnectorIcon, never
// a Google mark.
const PLATFORM_DEFS = [
  {
    id: 'SHOPIFY',
    name: 'Shopify',
    description: 'Orders, products, and store performance',
    icon: ShopifyIcon,
    accountLabel: 'STORE',
    authComponent: ShopifyOnboardingAuth,
    requiredScopes: [],
  },
  {
    id: 'GOOGLEANALYTICS',
    name: 'Google Analytics',
    description: 'Sessions, conversions, and audience behavior',
    icon: GAIcon,
    accountLabel: 'ACCOUNT & PROPERTY',
    authComponent: GoogleAnalyticsAuth,
    isGoogleProduct: true,
    ctaLabel: 'Connect Google Analytics',
    ctaVariant: 'cliqmetrics',
    requiredScopes: [
      'https://www.googleapis.com/auth/analytics.readonly',
    ],
  },
  {
    id: 'GOOGLEADS',
    name: 'Google Ads',
    description: 'Campaign spend, ROAS, and conversions',
    icon: GenericConnectorIcon,
    accountLabel: 'CUSTOMER ID',
    authComponent: GoogleAdsAuth,
    isGoogleProduct: true,
    ctaLabel: 'Connect Google Ads',
    ctaVariant: 'cliqmetrics',
    requiredScopes: [
      'https://www.googleapis.com/auth/adwords',
      'https://www.googleapis.com/auth/cloud-platform',
      'https://www.googleapis.com/auth/bigquery',
    ],
  },
  {
    id: 'GOOGLESEARCHCONSOLE',
    name: 'Google Search Console',
    description: 'Organic impressions, clicks, and CTR',
    icon: GenericConnectorIcon,
    accountLabel: 'PROPERTY',
    authComponent: GoogleSearchConsoleAuth,
    isGoogleProduct: true,
    ctaLabel: 'Connect Google Search Console',
    ctaVariant: 'cliqmetrics',
    requiredScopes: [
      'https://www.googleapis.com/auth/webmasters.readonly',
      'https://www.googleapis.com/auth/webmasters',
    ],
  },
  {
    id: 'GOOGLE_MERCHANT_CENTER',
    name: 'Google Merchant Center',
    description: 'Product listings and shopping performance',
    icon: GenericConnectorIcon,
    accountLabel: 'MERCHANT ID',
    authComponent: GoogleMerchantCenterAuth,
    isGoogleProduct: true,
    ctaLabel: 'Connect Google Merchant Center',
    ctaVariant: 'cliqmetrics',
    requiredScopes: [],
  },
  // ── Meta platforms ──
  // TRD grant-access-connector-branding/trd.md, R2/D6/D7/D8 -- the old
  // single combo "Meta" tile is split into two brand-compliant tiles
  // (M1/I5). Both keep the SAME unchanged `authComponent: MetaAuth` (D7) --
  // one unified Facebook Login (for Business) dialog still drives both;
  // MetaAuth.jsx already internally distinguishes Pages/Ad Accounts/
  // Instagram Accounts in its own asset-review step. `id` values are
  // synthetic (D6, matching the `id: 'META'` synthetic-id precedent) so
  // neither collides with a real `data_sources.provider` value -- in
  // particular, `'META_INSTAGRAM'` deliberately does not equal the dormant
  // legacy provider string `'INSTAGRAM'` (distinct from the live
  // `'INSTAACCOUNT'` this tile actually matches against, see
  // docs/schema/tables/data_sources.md). Connection state is still derived
  // entirely from `matchProviders` (fetchDataSources below), never from id.
  // Revoke-picker plumbing (handleRevokeRequest/RevokeProductPicker/
  // META_PRODUCT_LABELS) needs zero new logic for the split (D8) -- it
  // already branches on `platform.matchProviders` generically.
  {
    id: 'META_FACEBOOK',
    name: 'Facebook',
    description: 'Facebook Pages and ad performance',
    icon: FacebookIcon,
    accountLabel: 'CONNECTED',
    authComponent: MetaAuth,
    matchProviders: ['FACEBOOKPAGES', 'METAADS'],
    ctaLabel: 'Continue with Facebook',
    ctaVariant: 'facebook',
    requiredScopes: [],
  },
  {
    id: 'META_INSTAGRAM',
    name: 'Instagram',
    description: 'Instagram performance and engagement',
    icon: InstagramIcon,
    accountLabel: 'CONNECTED',
    authComponent: MetaAuth,
    matchProviders: ['INSTAACCOUNT'],
    ctaLabel: 'Connect Instagram',
    ctaVariant: 'instagram',
    requiredScopes: [],
  },
];

// TRD: oauth-credential-product-granularity/trd.md, Section 0.4/3.3 — display
// labels for the individual products the Meta combo tile aggregates, used by
// RevokeProductPicker so a user revoking "Meta" can tell which underlying
// data_sources row ("Facebook Pages" vs "Meta Ads" vs "Instagram") each
// revoke action targets.
const META_PRODUCT_LABELS = {
  FACEBOOKPAGES: 'Facebook Pages',
  METAADS: 'Meta Ads',
  INSTAACCOUNT: 'Instagram',
};

/* ─── Three-dot menu ─────────────────────────────────────────────── */
// `canRevoke` (Project Role-Enforcement Middleware TRD, Section 3.2) —
// derived from currentProject.role at the GrantOnboardingAccess level and
// threaded down here. "Revoke access" renders disabled (not removed from
// the DOM) when false, same disabled-not-hidden treatment used for the Team
// tab's action menu (Settings.jsx, TRD Section 3.1) for consistency across
// this unit's surfaces. "Reconnect / switch account" is out of Rule 3's
// scope and stays unconditionally enabled.
const ThreeDotMenu = ({ onReconnect, onRevoke, canRevoke = true }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        className={styles.menuBtn}
        onClick={(e) => {
          e.stopPropagation();
          setOpen((o) => !o);
        }}
        aria-label="More options"
      >
        <Icon name="more_horiz" size={16} color="var(--color-on-surface-variant)" />
      </button>
      {open && (
        <div className={styles.menuDropdown}>
          <button
            className={styles.menuItem}
            onClick={() => {
              setOpen(false);
              onReconnect();
            }}
          >
            <Icon name="sync" size={15} color="var(--color-on-surface-variant)" />
            Reconnect / switch account
          </button>
          <button
            className={`${styles.menuItem} ${styles.menuItemDanger} ${!canRevoke ? styles.menuItemDisabled : ''}`}
            disabled={!canRevoke}
            title={!canRevoke ? 'Only Owners and Admins can manage data source access.' : undefined}
            onClick={() => {
              if (!canRevoke) return;
              setOpen(false);
              onRevoke();
            }}
          >
            <Icon name="link_off" size={15} color={canRevoke ? 'var(--color-error)' : 'var(--color-on-surface-muted)'} />
            Revoke access
          </button>
        </div>
      )}
    </div>
  );
};

/* ─── Revoke confirmation modal ──────────────────────────────────── */
// `canRevoke` defaults true so this modal's own signature stays backward
// compatible; in practice it's only ever reached via ThreeDotMenu's "Revoke
// access" item, which is itself already disabled when canRevoke is false —
// this is defense-in-depth (TRD Section 3.2 threads canRevoke to
// ThreeDotMenu/RevokeModal/PlatformTile, not just the menu item).
const RevokeModal = ({ platformName, onConfirm, onCancel, canRevoke = true }) => (
  <div className={styles.modalOverlay} onClick={onCancel}>
    <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
      <div className={styles.modalWarningIcon}>
        <Icon name="warning" size={24} color="var(--color-tertiary)" />
      </div>
      <h3 className={styles.modalTitle}>Revoke access to {platformName}?</h3>
      <p className={styles.modalBody}>
        Dashboards and reports that rely on {platformName} data will stop
        refreshing. You can reconnect at any time from Integrations.
      </p>
      <div className={styles.modalInfoBox}>
        <Icon name="info" size={15} color="var(--color-primary)" style={{ flexShrink: 0 }} />
        <span>
          Historical data already pulled into Cliqmetrics stays available — only new syncs will stop.
        </span>
      </div>
      <div className={styles.modalActions}>
        <button className={styles.revokeBtn} onClick={onConfirm} disabled={!canRevoke}>
          Revoke access
        </button>
        <button className={styles.keepBtn} onClick={onCancel}>
          <Icon name="check" size={15} color="#fff" style={{ marginRight: 6 }} />
          Keep connected
        </button>
      </div>
    </div>
  </div>
);

/* ─── Reconnect confirmation modal ───────────────────────────────── */
// Bug fix: datasource-reconnect-menu-noop/trd.md, Requirement 1 (Gap A) --
// mirrors RevokeModal's structural pattern exactly. Confirming deletes the
// existing connection's credentials immediately (via the same
// revokeDataSources endpoint RevokeModal uses), then triggers a fresh OAuth
// grant on success -- see handleReconnectConfirm below.
const ReconnectModal = ({ platformName, onConfirm, onCancel, isReconnecting = false }) => (
  <div className={styles.modalOverlay} onClick={onCancel}>
    <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
      <div className={styles.modalWarningIcon}>
        <Icon name="warning" size={24} color="var(--color-tertiary)" />
      </div>
      <h3 className={styles.modalTitle}>Reconnect {platformName}?</h3>
      <p className={styles.modalBody}>
        Your existing {platformName} connection will be deleted immediately,
        and you'll be asked to sign in and grant access again.
      </p>
      <div className={styles.modalInfoBox}>
        <Icon name="info" size={15} color="var(--color-primary)" style={{ flexShrink: 0 }} />
        <span>Historical data already pulled into Cliqmetrics stays available while you reconnect.</span>
      </div>
      <div className={styles.modalActions}>
        <button className={styles.revokeBtn} onClick={onConfirm} disabled={isReconnecting}>
          {isReconnecting ? 'Reconnecting…' : 'Reconnect'}
        </button>
        <button className={styles.keepBtn} onClick={onCancel}>
          <Icon name="check" size={15} color="#fff" style={{ marginRight: 6 }} />
          Cancel
        </button>
      </div>
    </div>
  </div>
);

/* ─── Per-product revoke picker (combo tiles only) ──────────────────── */
// TRD: oauth-credential-product-granularity/trd.md, Section 0.4/3.3 — a
// combo tile (currently just Meta — see fetchDataSources' matchProviders
// branch) can represent more than one independently-connected
// data_sources row under one grant. Revoking must let the user target
// exactly one of those rows, not default to every row the tile displays
// (AC12). This picker is only shown when the tile currently has more than
// one connected product; with exactly one, there's nothing to disambiguate
// so the caller skips straight to RevokeModal for that single row (see
// PlatformTile's onRevoke wiring below).
const RevokeProductPicker = ({ platform, onSelect, onCancel, canRevoke = true }) => (
  <div className={styles.modalOverlay} onClick={onCancel}>
    <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
      <h3 className={styles.modalTitle}>Revoke which {platform.name} connection?</h3>
      <p className={styles.modalBody}>
        {platform.name} has more than one connected product under this grant.
        Choose the one to revoke — the others stay connected.
      </p>
      <ul className={styles.revokeProductList}>
        {(platform.matchedDataSources || []).map((row) => (
          <li key={row.id} className={styles.revokeProductRow}>
            <span className={styles.revokeProductName}>
              {META_PRODUCT_LABELS[row.provider] || row.provider}
            </span>
            <button
              type="button"
              className={styles.revokeProductBtn}
              disabled={!canRevoke}
              title={!canRevoke ? 'Only Owners and Admins can manage data source access.' : undefined}
              onClick={() => onSelect(row)}
            >
              Revoke
            </button>
          </li>
        ))}
      </ul>
      <div className={styles.modalActions}>
        <button className={styles.keepBtn} onClick={onCancel}>
          <Icon name="check" size={15} color="#fff" style={{ marginRight: 6 }} />
          Cancel
        </button>
      </div>
    </div>
  </div>
);

/* ─── Platform tile ──────────────────────────────────────────────── */
// `locked` gates every non-Shopify tile until Shopify access is granted —
// Shopify must be the first platform a project connects. A locked tile is
// greyed out and inert (no click, no hover) regardless of its own connection
// state (a locked tile is never itself the connected one, since the only
// platform gating the others is Shopify itself, which is never locked).
const PlatformTile = ({ platform, grantedScopes, providerProjectId, project, onUpdate, onAuthRequest, onReconnectRequest, onRevoke, locked, canRevoke = true, isLoading = false }) => {
  const [hovered, setHovered] = useState(false);

  const PlatformIcon = platform.icon;
  const isShopify = platform.id === 'SHOPIFY';
  const isGoogleProduct = !!platform.isGoogleProduct;

  // A platform is connected once the backend has a datasource record for it,
  // indicated by providerProjectId being set. The backend validates OAuth
  // scopes during the auth flow, so a non-null providerProjectId is the
  // authoritative signal that access has been granted.
  const connected = !platform.comingSoon && providerProjectId != null;
  const inert = connected || platform.comingSoon || locked;

  const handleTileClick = () => {
    if (inert || !platform.authComponent) return;
    onAuthRequest(platform);
  };

  // TRD grant-access-connector-branding/trd.md, R3 -- this button carries
  // the "Connect <name>" affordance/aria-label every tile has always had
  // (AC1's `button[aria-label^="Connect"]` contract). For a Google-product
  // tile (R4/AC7) it's rendered smaller, inside the demoted product row,
  // instead of as the tile's big top-level icon -- but it stays the exact
  // same button element/click wiring either way, so no existing or new
  // "Connect Google Analytics"-style assertion (contract test, unit tests)
  // needs different selectors depending on which layout renders it.
  const iconButton = (
    <button
      className={isGoogleProduct ? styles.demotedIconBtn : styles.iconBtn}
      tabIndex={inert ? -1 : 0}
      onClick={(e) => {
        e.stopPropagation();
        handleTileClick();
      }}
      aria-label={locked ? `Connect Shopify before ${platform.name}` : `Connect ${platform.name}`}
      aria-disabled={inert}
      disabled={locked}
      style={{ cursor: inert ? 'default' : 'pointer' }}
    >
      <PlatformIcon size={isGoogleProduct ? 22 : 32} wrapperStyle={{ padding: 0 }} />
    </button>
  );

  return (
    <div
      className={`${styles.tile} ${locked ? styles.tileLocked : ''}`}
      style={{
        cursor: inert ? 'default' : 'pointer',
        outline: connected ? '1.5px solid rgba(0,104,94,0.18)' : 'none',
        boxShadow: hovered && !inert
          ? '0 14px 36px rgba(0,131,119,0.18), 0 2px 6px rgba(0,104,94,0.08)'
          : '0 8px 20px rgba(0,131,119,0.10), 0 1px 2px rgba(0,104,94,0.05)',
        transform: hovered && !inert
          ? 'translateY(-1px)'
          : 'none',
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onClick={handleTileClick}
    >
      {/* Grant-access loader centralization (requirement 3): shown while
          THIS platform's own account-list fetch (after the OAuth popup
          closes) or account-save call is in flight -- driven by the auth
          component's onLoadingChange callback, aggregated per-platform-id
          by the parent. `.tile` is already position:relative, so this
          overlay covers exactly the tile the user is interacting with, not
          the whole grid. Same shared PageLoader used for every other
          loading state in the app (overlay variant). */}
      {isLoading && (
        <PageLoader overlay size="sm" label={null} />
      )}
      {/* Top badges row */}
      <div className={styles.tileBadgeRow}>
        {connected && (
          <>
            <div className={styles.connectedBadge}>
              <Icon name="check" size={13} color="#fff" />
            </div>
            <ThreeDotMenu
              onReconnect={() => onReconnectRequest(platform)}
              onRevoke={() => onRevoke(platform)}
              canRevoke={canRevoke}
            />
          </>
        )}
        {!connected && platform.comingSoon && (
          <span className={styles.comingSoonBadge}>Soon</span>
        )}
        {!connected && !platform.comingSoon && locked && (
          <span className={styles.lockedBadge}>
            <Icon name="lock" size={11} color="var(--color-on-surface-muted)" style={{ marginRight: 3, verticalAlign: '-2px' }} />
            Connect Shopify first
          </span>
        )}
      </div>

      {/* TRD R4 (G13/G14) -- on the four Google tiles, a CliqMetrics
          "eyebrow" (brand chip + wordmark) is the dominant top element, with
          the Google product demoted to a small icon + label row beneath it
          (AC7: the Google icon appears only inside this demoted row, never
          standalone as the tile's primary visual). Every other tile keeps
          the classic big icon-button-first layout. */}
      {isGoogleProduct ? (
        <>
          <div className={styles.cmEyebrow}>
            <span className={styles.cmEyebrowChip} aria-hidden="true" />
            <span className={styles.cmEyebrowWordmark}>Cliqmetrics</span>
          </div>
          <div className={styles.googleRow}>
            {iconButton}
            <span className={styles.googleRowName}>{platform.name}</span>
          </div>
        </>
      ) : (
        iconButton
      )}

      {/* Name & description -- Google tiles already show their name in the
          demoted row above, so the standalone tileName is skipped for them
          (no duplicate product name). */}
      {!isGoogleProduct && <div className={styles.tileName}>{platform.name}</div>}
      <div className={styles.tileDesc}>{platform.description}</div>

      {/* TRD R5 -- Google Analytics' required "logo links back to Google
          Analytics" element (G16). Uses the already-exported
          GOOGLE_ANALYTICS_URL constant from BrandLogos/GoogleAnalytics. */}
      {platform.id === 'GOOGLEANALYTICS' && (
        <a
          href={GOOGLE_ANALYTICS_URL}
          target="_blank"
          rel="noopener noreferrer"
          className={styles.gaLinkBack}
          onClick={(e) => e.stopPropagation()}
        >
          Logo links to Google Analytics
        </a>
      )}

      {/* Footer -- Shopify keeps its existing, pixel-for-pixel-unchanged
          footer treatment (PRD Section 2, out of scope for the brand-CTA
          rework). Every other tile's not-yet-connected state now renders a
          real branded CTA button (R3/AC2/AC3/AC9/AC10/AC12) instead of the
          old "click icon to connect" caption; connected/coming-soon states
          are unchanged for all tiles. */}
      {isShopify ? (
        <div className={styles.tileFooter}>
          {connected ? (
            <div className={styles.connectedInfo}>
              <span className={styles.accountLabel}>{platform.accountLabel}</span>
              <span className={styles.accountValue}>{providerProjectId || '—'}</span>
            </div>
          ) : locked ? (
            <span className={styles.comingSoonLabel}>Connect Shopify first</span>
          ) : (
            <div
              className={styles.connectLabel}
              style={{ color: hovered ? 'var(--color-primary)' : 'var(--color-on-surface-muted)' }}
            >
              <Icon
                name="add"
                size={13}
                color={hovered ? 'var(--color-primary)' : 'var(--color-on-surface-muted)'}
              />
              Click icon to connect
            </div>
          )}
        </div>
      ) : (
        <div className={styles.tileFooter}>
          {connected ? (
            <div className={styles.connectedInfo}>
              <span className={styles.accountLabel}>{platform.accountLabel}</span>
              <span className={styles.accountValue}>{providerProjectId || '—'}</span>
            </div>
          ) : platform.comingSoon ? (
            <span className={styles.comingSoonLabel}>Coming soon</span>
          ) : (
            // AC12 -- while locked, the CTA button is present but inert
            // (aria-disabled, tabIndex -1, non-clickable), matching the
            // icon button's existing locked treatment exactly rather than
            // swapping in different locked copy/visual.
            <button
              type="button"
              className={`${styles.ctaBtn} ${styles[`ctaBtn_${platform.ctaVariant}`] || ''}`}
              disabled={locked}
              aria-disabled={locked}
              tabIndex={locked ? -1 : 0}
              onClick={(e) => {
                e.stopPropagation();
                handleTileClick();
              }}
            >
              {platform.ctaLabel}
            </button>
          )}
        </div>
      )}
    </div>
  );
};

/* ─── GrantOnboardingAccess ──────────────────────────────────────── */
// `embedded` adapts this component for use outside the onboarding flow (e.g.
// the Settings → Datasources tab): it swaps the onboarding-specific
// header copy for generic copy and hides the "Finish anyway" bottom bar
// (which navigates into the onboarding stepper's next step and makes no
// sense embedded in a settings tab). All data-fetching/auth/revoke logic
// is unchanged and shared identically across both contexts.
const GrantOnboardingAccess = ({ organization, project, embedded = false }) => {
  const navigate = useNavigate();
  const location = useLocation();

  const [platformData, setPlatformData] = useState(() =>
    PLATFORM_DEFS.map((p) => ({ ...p, grantedScopes: [], providerProjectId: null }))
  );

  // Grant-access loader centralization (requirement 3): platform id -> bool,
  // fed by each auth component's onLoadingChange callback (account-list
  // fetch after the OAuth popup closes, and the final account-save call --
  // both currently render nothing at all on their own). One shared handler
  // and one shared per-tile overlay (PlatformTile, above) rather than each
  // of the five connectors inventing its own loading visual.
  const [loadingPlatformIds, setLoadingPlatformIds] = useState(() => new Set());

  const handlePlatformLoadingChange = useCallback((platformId, isLoading) => {
    setLoadingPlatformIds((prev) => {
      const isCurrentlyLoading = prev.has(platformId);
      if (isCurrentlyLoading === isLoading) return prev;
      const next = new Set(prev);
      if (isLoading) next.add(platformId);
      else next.delete(platformId);
      return next;
    });
  }, []);

  // loginRefs stores each platform's login() function once auth components mount.
  // Calling it from a tile click preserves the user gesture context → no popup blocking.
  const loginRefs = useRef({});
  const [revokePlatform, setRevokePlatform] = useState(null);
  // TRD: oauth-credential-product-granularity/trd.md, Section 0.4/3.3 — set
  // only when a combo tile (Meta) has more than one connected product and
  // the user needs to pick exactly one to revoke; see handleRevokeRequest.
  const [revokeProductPicker, setRevokeProductPicker] = useState(null);
  const [revokeError, setRevokeError] = useState(null);

  // Bug fix: datasource-reconnect-menu-noop/trd.md, Requirement 1 (Gap A) --
  // mirrors revokePlatform/revokeError above. { name, dataSourceId,
  // platformId } once a reconnect is requested from any tile's ThreeDotMenu.
  const [reconnectPlatform, setReconnectPlatform] = useState(null);
  const [reconnectError, setReconnectError] = useState(null);
  const [isReconnecting, setIsReconnecting] = useState(false);

  // Multi-Platform Business Support TRD, Bounded Unit B, Requirement B3 —
  // Shopify tile click opens this dialog instead of redirecting straight to
  // OAuth (Shopify-tile-only; every other tile is unaffected, AC-B3.5).
  const [shopifyDialogOpen, setShopifyDialogOpen] = useState(false);

  // TRD grant-access-connector-branding/trd.md, R7 -- the tile row is a
  // horizontally-scrolling carousel with a 2-dot pagination indicator that
  // reflects scroll position (design reference: iteration-3.png). `gridRef`
  // is the scroll container; `activePage` is 0 (start) or 1 (end) — the
  // design's initial view fits 6 tiles, with the 7th (Instagram) reachable
  // by scrolling to the second "page". Two dots is intentionally a coarse
  // start/end indicator, not one dot per tile.
  const gridRef = useRef(null);
  const [activePage, setActivePage] = useState(0);

  const handleGridScroll = useCallback(() => {
    const el = gridRef.current;
    if (!el) return;
    const maxScroll = el.scrollWidth - el.clientWidth;
    if (maxScroll <= 0) {
      setActivePage(0);
      return;
    }
    setActivePage(el.scrollLeft > maxScroll / 2 ? 1 : 0);
  }, []);

  const scrollToPage = useCallback((page) => {
    const el = gridRef.current;
    if (!el) return;
    const maxScroll = el.scrollWidth - el.clientWidth;
    el.scrollTo({ left: page === 0 ? 0 : maxScroll, behavior: 'smooth' });
  }, []);

  // Requirement B6 (UX-context, PRD §3.1.4's "dashboards-unlocking moment")
  // — an on-screen acknowledgment fires the first time any platform
  // transitions from not-connected to connected while this component is
  // mounted. This covers the four non-Shopify connectors, which use a popup
  // OAuth flow (GoogleDataAccess/react-oauth) that keeps this component
  // mounted throughout, so the diff effect below sees the transition
  // directly (AC-B6.1). Not fired for platforms that were already connected
  // on initial mount/fetch.
  const [justConnectedPlatform, setJustConnectedPlatform] = useState(null);
  const connectedIdsRef = useRef(null);

  // Shopify is different: its OAuth is a same-tab full-page redirect (see
  // ShopifyUrlDialog), so this component unmounts and remounts fresh on
  // return -- the diff effect above would only ever see Shopify already
  // connected on its very first (baseline) fetch and would never fire.
  // cq-api's shopify.router.js '/callback' success branch redirects back to
  // `${FRONTEND_URL}/${projectId}/grant-access?provider=shopify&status=success`,
  // so detect that query-param pair on mount instead (AC-B6.2 -- the
  // design's own connect-acknowledgment frame for this path).
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('provider') === 'shopify' && params.get('status') === 'success') {
      setJustConnectedPlatform('SHOPIFY');
    }
    // Fires once per mount only -- intentionally not re-run on subsequent
    // location.search changes within the same mounted session.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Project Role-Enforcement Middleware TRD, Section 3.2 — Rule 3 gates
  // "Revoke access" to OWNER/ADMIN only (client-side mirror of the
  // server-side DATASOURCE_REVOKE_ACCESS gate). `project` here is
  // whatever the caller passed in -- Settings.jsx's Datasources tab passes
  // currentProject, and the onboarding flow (GrantAccess.jsx) passes the
  // freshly-created project, whose creator always holds OWNER (see
  // project.service.js createProject) -- so this derivation is valid
  // in both contexts this component is used from.
  const canRevoke = project?.role === 'OWNER' || project?.role === 'ADMIN';

  const fetchDataSources = useCallback(() => {
    getCMResponse('/datasource/get-all-data-sources', 'GET', {
      project_id: project.id,
    })
      .then((response) => {
        if (response) {
          setPlatformData((prev) =>
            prev.map((platform) => {
              // Tiles backed by multiple provider values (currently just the
              // unified Meta tile) aggregate across all of them rather than
              // matching a single `data_sources.provider` — a single OAuth
              // grant can now save several rows across several providers.
              // `providerProjectId` here becomes a summary count string
              // rather than a raw ID; it's still just used as the
              // "connected" signal (non-null) and footer display text, so no
              // change to PlatformTile's rendering is needed.
              if (platform.matchProviders) {
                // Bug fix: grant-access-ga-live-call-on-load/trd.md, Section
                // 1, R2 -- canonical "connected" check (tile-rendering /
                // providerProjectId / isConnected) is now `status === 'ACTIVE'`
                // (Scoping Decision (a)), not `has_valid_credential`. This is
                // a distinct question from "does this row have a completed
                // OAuth grant eligible to be revoked", which is what the
                // revoke-picker's row list (`matchedDataSources`) answers --
                // per TRD Section 1.1's explicit callout, that stays on
                // `has_valid_credential` unchanged, so it needs its own
                // separately-filtered list rather than sharing `rows` with
                // the connected-check below.
                const connectedRows = response.filter(
                  (d) => platform.matchProviders.includes(d.provider) && d.status === 'ACTIVE'
                );
                // Bug fix: shopify-auth-false-positive-grant/trd.md, Section
                // 2, R5 -- a token-less data_sources row (e.g. dummy-builder
                // demo-seed data) must not count as connected. Filter to
                // rows with a completed OAuth grant before deriving the
                // revoke-picker's row list.
                const revokableRows = response.filter(
                  (d) => platform.matchProviders.includes(d.provider) && d.has_valid_credential
                );
                return {
                  ...platform,
                  providerProjectId: connectedRows.length > 0
                    ? `${connectedRows.length} asset${connectedRows.length === 1 ? '' : 's'}`
                    : null,
                  isConnected: connectedRows.length > 0,
                  grantedScopes: [],
                  // TRD: oauth-credential-product-granularity/trd.md,
                  // Section 0.4/3.3 — per-product revoke selection needs to
                  // know which data_sources row belongs to which provider,
                  // not just the flat id list the old
                  // "revoke everything this tile matches" behavior used.
                  matchedDataSources: revokableRows.map((r) => ({ id: r.id, provider: r.provider })),
                };
              }
              const ds = response.find((d) => d.provider === platform.id);
              // Bug fix: grant-access-ga-live-call-on-load/trd.md, Section
              // 1, R2 -- canonical "connected" check migrated from
              // `has_valid_credential` to `status === 'ACTIVE'` (Scoping
              // Decision (a)). Per the TRD, `status` and `has_valid_credential`
              // are documented to move together in current production data,
              // so this preserves the shopify-auth-false-positive-grant
              // fix's "no completed grant -> not-connected" behavior
              // (AC5) while also correctly treating an
              // access-token-present-but-not-yet-ACTIVE row (e.g. an
              // in-progress grant, or a dummy-builder demo-seed row) as
              // not-connected (AC3) -- the deliberate behavior change this
              // bug fix introduces.
              const isConnected = ds?.status === 'ACTIVE';
              return {
                ...platform,
                providerProjectId: isConnected ? ds.provider_project_id : null,
                isConnected,
                grantedScopes: ds?.scopes || [],
                // Retained so handleRevoke has a concrete id to send --
                // previously discarded once providerProjectId/scopes
                // were extracted. Gated the same way as providerProjectId
                // above -- a not-connected row's tile is !connected, so
                // ThreeDotMenu/revoke never renders for it anyway; this
                // keeps dataSourceId consistent with that state rather than
                // silently carrying a stale, unusable id.
                dataSourceId: isConnected ? ds.id : null,
              };
            })
          );
        }
      })
      .catch((err) => {
        console.error('Error fetching data sources:', err);
      });
  }, [project.id]);

  useEffect(() => {
    fetchDataSources();
  }, [fetchDataSources]);

  // Requirement B6 -- detect newly-connected platforms after every
  // fetchDataSources resolution and surface an on-screen acknowledgment.
  // connectedIdsRef starts as `null` so the very first fetch (initial page
  // load) only seeds the baseline and never fires a banner for platforms
  // that were already connected before this component mounted.
  useEffect(() => {
    const currentlyConnected = new Set(
      platformData.filter((p) => p.providerProjectId != null).map((p) => p.id)
    );
    if (connectedIdsRef.current === null) {
      connectedIdsRef.current = currentlyConnected;
      return;
    }
    const newlyConnected = [...currentlyConnected].find((id) => !connectedIdsRef.current.has(id));
    connectedIdsRef.current = currentlyConnected;
    if (newlyConnected) {
      setJustConnectedPlatform(newlyConnected);
    }
  }, [platformData]);

  // TRD: oauth-credential-product-granularity/trd.md, Section 0.4/3.3
  // (supersedes the old "revoke every row this tile matches" behavior from
  // dashboard-data-source-dependency-model/trd.md, Section 4.1 Requirement
  // 4/item 2 — per-product independent credentials mean there is no longer
  // a shared grant to batch-revoke as one unit; see AC4/AC12). `revokePlatform`
  // is always shaped as a single product by the time this runs — either the
  // single-provider tile itself (dataSourceId), or the one row the user
  // picked out of a combo tile via RevokeProductPicker/the single-connected-
  // product shortcut in handleRevokeRequest below. Failure is logged but
  // does not leave the modal stuck open -- the alternative (leaving it open
  // with no retry affordance) is worse UX than a silent close plus the
  // tile simply not updating, and the PRD specifies no explicit
  // failure-handling UI for this action.
  const handleRevoke = useCallback(() => {
    if (!revokePlatform) return;
    setRevokeError(null);
    revokeDataSources(project.id, [revokePlatform.dataSourceId].filter(Boolean))
      .then(() => {
        setRevokePlatform(null);
        fetchDataSources();
      })
      .catch((err) => {
        console.error('Error revoking access:', err);
        // Project Role-Enforcement Middleware TRD, Section 3.2 -- a 403
        // here means a stale client (role changed mid-session); surface it
        // via this app's existing error-toast pattern (there is no
        // dedicated Toast/Snackbar component anywhere in cq-app, confirmed
        // via repo search -- the existing pattern is this inline error
        // message, same convention Settings.jsx uses). Any other error
        // keeps the pre-existing behavior exactly (silent close, console.error
        // only) -- unchanged from before this unit.
        if (err.response?.status === 403) {
          setRevokeError(err.response?.data?.message || 'You don’t have permission to revoke access to this data source.');
        }
        setRevokePlatform(null);
      });
  }, [revokePlatform, project.id, fetchDataSources]);

  // TRD: oauth-credential-product-granularity/trd.md, Section 0.4/3.3 —
  // entry point wired to PlatformTile's ThreeDotMenu "Revoke access". For a
  // plain single-provider tile, go straight to the existing RevokeModal
  // (unchanged UX/AC8 case). For a combo tile (Meta): with 0-1 connected
  // products there's nothing to disambiguate, so it also goes straight to
  // RevokeModal for that one row; with 2+, open RevokeProductPicker so the
  // user targets exactly one row without ever defaulting to "revoke all"
  // (AC12) -- see PLATFORM_DEFS: only the Meta tile currently sets
  // matchProviders, the Google products are already one-tile-per-provider
  // and need no picker.
  const handleRevokeRequest = useCallback((platform) => {
    if (!platform.matchProviders) {
      setRevokePlatform(platform);
      return;
    }
    const rows = platform.matchedDataSources || [];
    if (rows.length > 1) {
      setRevokeProductPicker(platform);
      return;
    }
    const only = rows[0];
    setRevokePlatform({
      name: only ? `${platform.name} — ${META_PRODUCT_LABELS[only.provider] || only.provider}` : platform.name,
      dataSourceId: only?.id,
    });
  }, []);

  const handleRevokeProductSelect = useCallback((row) => {
    if (!revokeProductPicker) return;
    setRevokePlatform({
      name: `${revokeProductPicker.name} — ${META_PRODUCT_LABELS[row.provider] || row.provider}`,
      dataSourceId: row.id,
    });
    setRevokeProductPicker(null);
  }, [revokeProductPicker]);

  // Bug fix: datasource-reconnect-menu-noop/trd.md, Requirement 1 (Gap A) --
  // entry point wired to PlatformTile's ThreeDotMenu "Reconnect / switch
  // account" for every platform tile (platform-wide, per the report's
  // resolved Open Item 1/2). Opens the confirmation modal instead of
  // triggering OAuth directly.
  const handleReconnectRequest = useCallback((platform) => {
    setReconnectPlatform({
      name: platform.name,
      dataSourceId: platform.dataSourceId,
      platformId: platform.id,
    });
  }, []);

  // On Confirm: delete the existing connection's credentials immediately
  // (delete-first sequencing, per the report's resolved Open Item 1), then
  // on success trigger a fresh OAuth grant exactly like the initial
  // connection flow -- same loginRefs/setShopifyDialogOpen mechanism
  // `onAuthRequest` uses. Mirrors handleRevoke's shape.
  const handleReconnectConfirm = useCallback(() => {
    if (!reconnectPlatform) return;
    setReconnectError(null);
    setIsReconnecting(true);
    revokeDataSources(project.id, [reconnectPlatform.dataSourceId].filter(Boolean))
      .then(() => {
        setReconnectPlatform(null);
        setIsReconnecting(false);
        fetchDataSources();
        // Fire the fresh OAuth grant immediately on delete success, exactly
        // like a new connection -- same mechanism as the initial grant flow.
        if (reconnectPlatform.platformId === 'SHOPIFY') {
          setShopifyDialogOpen(true);
        } else {
          loginRefs.current[reconnectPlatform.platformId]?.();
        }
      })
      .catch((err) => {
        console.error('Error reconnecting:', err);
        if (err.response?.status === 403) {
          setReconnectError(err.response?.data?.message || 'You don’t have permission to reconnect this data source.');
        }
        setIsReconnecting(false);
        setReconnectPlatform(null);
      });
  }, [reconnectPlatform, project.id, fetchDataSources]);

  const handleReconnectCancel = useCallback(() => setReconnectPlatform(null), []);

  // Multi-Platform Business Support TRD, Bounded Unit B, Requirement B4 —
  // the Shopify tile is filtered out entirely for a non-Shopify-type project
  // (PRD §3.2's "additive, not exclusive" gate). Every other tile always
  // renders regardless of project_type. Applied identically whether this
  // component is reached via the standalone /grant-access route or embedded
  // in Settings' Data Sources tab (both pass the same `project` prop through
  // to this one component -- no divergent logic needed, AC-B4.3).
  const isShopifyTypeProject = project?.project_type !== 'OTHER';
  const visiblePlatformData = platformData.filter(
    (p) => p.id !== 'SHOPIFY' || isShopifyTypeProject
  );

  const connectedCount = visiblePlatformData.filter(
    (p) => !p.comingSoon && p.providerProjectId != null
  ).length;

  const totalConnectable = visiblePlatformData.filter((p) => !p.comingSoon).length;
  const allConnected = connectedCount === totalConnectable;

  // Shopify must be the first platform a Shopify-type project connects —
  // every other tile stays locked (greyed out, not clickable) until the
  // Shopify tile's own providerProjectId is set. This re-derives from
  // platformData on every render, so it flips immediately once
  // fetchDataSources (called both on mount and after Shopify's
  // onGrantedScopesUpdate) reports Shopify connected — no extra state or
  // reload needed. For a non-Shopify-type project the Shopify tile doesn't
  // exist at all (B4), so this gate is meaningless there — every other tile
  // is unlocked immediately.
  const shopifyConnected = !isShopifyTypeProject || platformData.some(
    (p) => p.id === 'SHOPIFY' && p.providerProjectId != null
  );

  // All dashboard templates are now auto-enabled by default for every
  // project (see cq-api companion change), so the select-dashboards
  // screen is redundant in this flow -- "Finish anyway" goes straight to
  // the insights homepage (same DEFAULT_LANDING_ROUTE every other post-auth
  // path resolves to, see utils/postAuthRoute.js) instead of asking the
  // user to pick dashboards. The select-dashboards screen/route itself is
  // untouched -- AllDashboards.jsx still links to it for its own (currently
  // hidden behind SHOW_ADD_FROM_TEMPLATES) "Add from Templates" flow.
  const handleFinish = useCallback(() => {
    navigate(buildProjectPath(project?.id, '/analyze/insights'));
  }, [navigate, project]);

  return (
    <div className={styles.container}>
      {/* Header */}
      <div className={styles.header}>
        {!embedded && <p className={styles.eyebrow}>Step 02 — Data access</p>}
        <h2 className={styles.heading}>
          {embedded ? 'Data Sources' : 'Connect your data sources.'}
        </h2>
        <p className={styles.body}>
          {embedded
            ? "Connect your platforms to start pulling live data. Each platform allows a single account or property — you can change your selection later."
            : "Click a platform's icon to grant Cliqmetrics read access. Each platform allows a single account or property — you can change your selection later from Integrations."}
        </p>
      </div>

      {revokeError && <p className={styles.errorBanner}>{revokeError}</p>}
      {reconnectError && <p className={styles.errorBanner}>{reconnectError}</p>}

      {/* Requirement B6 -- "dashboards-unlocking moment" acknowledgment.
          Shopify gets the design's dedicated connect-acknowledgment copy
          (AC-B6.2); the four non-Shopify connectors share the same banner
          treatment for their existing OAuth-return flow (AC-B6.1), since no
          design artifact covers that case specifically. */}
      {justConnectedPlatform && (
        <div className={styles.connectAckBanner} role="status">
          <Icon name="check_circle" size={18} color="#22c55e" />
          <span>
            {justConnectedPlatform === 'SHOPIFY' ? (
              <>
                <strong>Shopify connected</strong> — Order, product, and
                store-performance dashboards are now unlocked, and the other
                connectors below are unlocked too.
              </>
            ) : (
              <>
                <strong>{(PLATFORM_DEFS.find((p) => p.id === justConnectedPlatform) || {}).name || 'Connector'} connected</strong>
                {' '}— dashboards and data for this connector are now unlocked.
              </>
            )}
          </span>
          <button
            type="button"
            className={styles.connectAckDismiss}
            onClick={() => setJustConnectedPlatform(null)}
            aria-label="Dismiss"
          >
            <Icon name="close" size={14} color="var(--color-on-surface-muted)" />
          </button>
        </div>
      )}

      {/* TRD grant-access-connector-branding/trd.md, R7 -- horizontally-
          scrolling tile carousel with 2-dot pagination (design reference:
          iteration-3.png), replacing the previous wrapping grid. */}
      <div className={styles.carouselWrapper}>
        <div className={styles.grid} ref={gridRef} onScroll={handleGridScroll}>
          {visiblePlatformData.map((platform) => (
            <PlatformTile
              key={platform.id}
              platform={platform}
              grantedScopes={platform.grantedScopes}
              providerProjectId={platform.providerProjectId}
              project={project}
              onUpdate={fetchDataSources}
              locked={platform.id !== 'SHOPIFY' && !shopifyConnected}
              onAuthRequest={(p) => {
                // Requirement B3 -- the Shopify tile opens the store-URL
                // dialog instead of redirecting straight into OAuth (both for
                // the initial connect click and for "Reconnect / switch
                // account", which also calls onAuthRequest). Every other tile
                // is unaffected (AC-B3.5).
                if (p.id === 'SHOPIFY') {
                  setShopifyDialogOpen(true);
                  return;
                }
                loginRefs.current[p.id]?.();
              }}
              onReconnectRequest={handleReconnectRequest}
              onRevoke={handleRevokeRequest}
              canRevoke={canRevoke}
              isLoading={loadingPlatformIds.has(platform.id)}
            />
          ))}
        </div>
        {visiblePlatformData.length > 1 && (
          <div className={styles.paginationDots} role="tablist" aria-label="Connector grid pages">
            {[0, 1].map((page) => (
              <button
                key={page}
                type="button"
                role="tab"
                aria-selected={activePage === page}
                aria-label={`Page ${page + 1}`}
                className={`${styles.dot} ${activePage === page ? styles.dotActive : ''}`}
                onClick={() => scrollToPage(page)}
              />
            ))}
          </div>
        )}
      </div>

      {shopifyDialogOpen && (
        <ShopifyUrlDialog
          project={project}
          onClose={() => setShopifyDialogOpen(false)}
        />
      )}

      {/* Auth components — always mounted outside the grid so they never create grid cells.
          onLoginReady stores each platform's login() into loginRefs so the tile's onClick
          can call it directly (same call stack as the user gesture → no popup blocking). */}
      <GoogleOAuthProvider clientId={Config.GOOGLE_AUTH_ID}>
        {platformData
          .filter((p) => p.authComponent && !p.comingSoon && p.id !== 'SHOPIFY')
          .map((p) => {
            const AuthComp = p.authComponent;
            return (
              <AuthComp
                key={p.id}
                onLoginReady={(fn) => { loginRefs.current[p.id] = fn; }}
                requiredScopes={p.requiredScopes}
                grantedScopes={p.grantedScopes}
                onGrantedScopesUpdate={fetchDataSources}
                project={project}
                providerProjectId={p.providerProjectId}
                // Bug fix: grant-access-ga-live-call-on-load/trd.md, Section
                // 1, R2 -- canonical connected signal, threaded into every
                // auth component for consistency/future-proofing. Only
                // GoogleAnalyticsAuth consumes it today (R1); the other four
                // receive it unread.
                isConnected={p.isConnected}
                onLoadingChange={(isLoading) => handlePlatformLoadingChange(p.id, isLoading)}
              />
            );
          })}
      </GoogleOAuthProvider>

      {/* Per-product revoke picker — combo tiles (Meta) with 2+ connected
          products; rendered outside the grid, ahead of RevokeModal below
          since it's the step before it in that flow. */}
      {revokeProductPicker && (
        <RevokeProductPicker
          platform={revokeProductPicker}
          onSelect={handleRevokeProductSelect}
          onCancel={() => setRevokeProductPicker(null)}
          canRevoke={canRevoke}
        />
      )}

      {/* Revoke modal — rendered outside the grid */}
      {revokePlatform && (
        <RevokeModal
          platformName={revokePlatform.name}
          onConfirm={handleRevoke}
          onCancel={() => setRevokePlatform(null)}
          canRevoke={canRevoke}
        />
      )}

      {/* Reconnect modal — rendered outside the grid */}
      {reconnectPlatform && (
        <ReconnectModal
          platformName={reconnectPlatform.name}
          onConfirm={handleReconnectConfirm}
          onCancel={handleReconnectCancel}
          isReconnecting={isReconnecting}
        />
      )}

      {/* Bottom bar — onboarding-only; embedded contexts (e.g. Settings tab)
          have no "next step" to finish into, so this whole bar is hidden. */}
      {!embedded && (
        <div className={styles.bottomBar}>
          <div className={styles.bottomBarLeft}>
            <span className={styles.bottomBarShield}>
              <Icon name="verified_user" size={22} color="#22c55e" />
            </span>
            <div className={styles.bottomBarInfo}>
              <span className={styles.bottomBarCount}>
                {allConnected
                  ? `All ${totalConnectable} data sources connected`
                  : `${connectedCount} of ${totalConnectable} data sources connected`}
              </span>
              <span className={styles.bottomBarSub}>
                You can finalize now and connect the rest later from integrations.
              </span>
            </div>
          </div>
          <button className={styles.finishBtn} onClick={handleFinish}>
            Finish anyway →
          </button>
        </div>
      )}
    </div>
  );
};

export default GrantOnboardingAccess;
